<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Container, Icon, ProgressBar, Text } from '@Shelly/shelly-design-system';
	import type { EnergyKpiCard } from 'src/components/EnergyMonitoring/helpers/energy_kpi_helpers';
	import EmInfoTooltip from '../EmInfoTooltip.svelte';

	export let card: EnergyKpiCard;

	const kpiInfoKeys: Record<string, string> = {
		load: 'energy_monitoring_kpi_load_info',
		solar: 'energy_monitoring_kpi_solar_info',
		selfPowered: 'energy_monitoring_kpi_self_powered_info',
		gridImport: 'energy_monitoring_kpi_grid_import_info',
		periodConsumption: 'energy_monitoring_kpi_period_consumption_info',
		periodGeneration: 'energy_monitoring_kpi_period_generation_info',
		tariff: 'energy_monitoring_kpi_tariff_info',
		currentTariff: 'energy_monitoring_kpi_current_tariff_info',
		offPeak: 'energy_monitoring_kpi_off_peak_info',
		topConsumer: 'energy_monitoring_kpi_top_consumer_info',
		standby: 'energy_monitoring_kpi_standby_info',
		peakDemand: 'energy_monitoring_kpi_peak_demand_info',
	};

	const kpiInfoDefaults: Record<string, string> = {
		load: 'Total power your home is drawing from all meters right now.',
		solar: 'Power your solar setup is generating at this moment.',
		selfPowered: 'Share of currently measured supply provided by solar.',
		gridImport: 'Power currently imported from the grid; export readings are excluded.',
		periodConsumption: 'Energy consumed during the selected period.',
		periodGeneration: 'Solar energy generated during the selected period.',
		tariff: 'Estimated electricity cost for today based on your tariff settings.',
		currentTariff: 'Tariff name and electricity rate active right now.',
		offPeak: 'Share of period consumption billed at the lowest configured rate.',
		topConsumer: 'Device currently drawing the most measured power.',
		standby: 'Combined live power of devices drawing no more than 50 W.',
		peakDemand: 'Highest consumption interval in the selected period.',
	};
</script>

<Container
	type="glass"
	noScroll
	padding="0"
	fullHeight
	class="energy-kpi-card-surface"
	style={`--kpi-color: var(${card.color}); border-radius: 16px; overflow: hidden;`}
>
	<div class="energy-kpi-card">
		<div class="energy-kpi-main">
			<div class="energy-kpi-copy">
				<div class="energy-kpi-label-row">
					<Text styleType="miniature" class="text-color-muted">
						{$format(card.labelKey ?? `energy_monitoring_kpi_${card.id}`, {
							default: card.infoText,
						})}
					</Text>
				</div>
				<Text styleType="h2" class="text-600">{card.value}</Text>
			</div>
			<div class="energy-kpi-actions">
				<div class="energy-kpi-icon">
					<Icon icon={card.icon} size="s" />
				</div>
				<EmInfoTooltip
					text={$format(kpiInfoKeys[card.id], {
						default: kpiInfoDefaults[card.id] ?? card.infoText,
					})}
				/>
			</div>
		</div>
		<div class="energy-kpi-detail">
			<Text styleType="body-small" class="text-600">{card.detail}</Text>
			{#if card.helper}
				<Text styleType="miniature" class="text-color-muted">{card.helper}</Text>
			{/if}
		</div>
		<div class="energy-kpi-footer">
			<div>
				<Text styleType="miniature" class="text-color-muted">
					{$format(card.statusLabelKey ?? `energy_monitoring_kpi_${card.id}_status`, {
						default: card.statusLabel,
					})}
				</Text>
				<Text styleType="body-small" class="text-600">{card.statusValue}</Text>
			</div>
			<ProgressBar progress={card.progress} type={card.progressType} />
		</div>
	</div>
</Container>

<style lang="scss">
	:global(.energy-kpi-card-surface.container) {
		position: relative;
		min-width: 0;
		border-radius: 16px;
		overflow: hidden;
	}

	.energy-kpi-card {
		position: relative;
		container-type: inline-size;
		display: grid;
		gap: var(--spacing-s);
		min-width: 0;
		height: 100%;
		padding: var(--spacing-s) var(--spacing-m);
		overflow: hidden;
	}

	.energy-kpi-card::before {
		content: '';
		position: absolute;
		inset: 0;
		background: linear-gradient(
			135deg,
			color-mix(in srgb, var(--kpi-color) 16%, transparent),
			transparent 70%
		);
		pointer-events: none;
	}

	.energy-kpi-card > * {
		position: relative;
	}

	.energy-kpi-main {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: var(--spacing-s);
		min-width: 0;
	}

	.energy-kpi-copy,
	.energy-kpi-detail,
	.energy-kpi-footer > div {
		min-width: 0;
	}

	.energy-kpi-label-row {
		display: inline-flex;
		align-items: center;
		gap: var(--spacing-xxs);
	}

	.energy-kpi-icon {
		display: grid;
		place-items: center;
		width: 28px;
		height: 28px;
		flex: 0 0 28px;
		border-radius: 50%;
		color: var(--kpi-color);
		background: color-mix(in srgb, var(--kpi-color) 14%, transparent);
	}

	.energy-kpi-actions {
		display: inline-flex;
		align-items: center;
		gap: var(--spacing-xs);
		flex: 0 0 auto;
	}

	.energy-kpi-detail,
	.energy-kpi-footer {
		display: none;
	}

	.energy-kpi-footer {
		align-items: end;
		grid-template-columns: minmax(0, 1fr);
		gap: var(--spacing-xs);
		padding-top: var(--spacing-xs);
		border-top: 1px solid rgba(255, 255, 255, 0.12);
	}

	@container (min-width: 190px) {
		.energy-kpi-detail {
			display: grid;
			gap: 2px;
		}
	}

	@container (min-width: 250px) {
		.energy-kpi-footer {
			display: grid;
		}
	}
</style>
